export function normalizeMailRecord(value = {}) {
  const message = value.message || value
  const result = value.latest_result || value.result || null
  return {
    ...value,
    message_id: value.message_id || message.message_id,
    sender: value.sender || message.sender || '',
    subject: value.subject || message.subject || '(No subject)',
    received_at: value.received_at || message.received_at || '',
    version: Number(value.version || 1),
    state: value.state || (result ? 'processed' : 'new'),
    attachment_count: Number(value.attachment_count ?? message.attachment_evidence?.length ?? 0),
    intent: value.intent || result?.intent || null,
    routing_bucket: value.routing_bucket || result?.routing_bucket || null,
    language: value.language || result?.language || null,
    linked_invoice_ids: value.linked_invoice_ids || result?.linked_invoice_ids || [],
  }
}

export function mailPresentation(record = {}) {
  const value = normalizeMailRecord(record)
  if (value.state === 'failed') return { label: 'Failed · retry', tone: 'danger' }
  if (value.state === 'processing') return { label: 'Processing', tone: 'warning' }
  if (value.intent === 'remittance_advice' && value.routing_bucket === 'customer_auto_handled') {
    return { label: 'Recorded · no reply', tone: 'success' }
  }
  if (value.state === 'processed' && value.routing_bucket === 'customer_needs_human') {
    return { label: 'Needs human review', tone: 'warning' }
  }
  if (value.state === 'processed') return { label: 'Processed', tone: 'success' }
  return { label: 'New', tone: 'neutral' }
}

async function sha256(bytes) {
  if (globalThis.crypto?.subtle) {
    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes)
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
  }
  let hash = 0x811c9dc5
  const view = new Uint8Array(bytes)
  for (let index = 0; index < view.length; index += 1) {
    hash ^= view[index]
    hash = Math.imul(hash, 0x01000193)
  }
  return `fnv-${(hash >>> 0).toString(16)}-${view.length}`
}

export async function mailUploadKey(files) {
  const entries = await Promise.all(Array.from(files || []).map(async (file) => ({
    name: file.name,
    size: file.size,
    digest: await sha256(await file.arrayBuffer()),
  })))
  entries.sort((left, right) => left.name.localeCompare(right.name) || left.digest.localeCompare(right.digest))
  const encoded = new TextEncoder().encode(entries.map((item) => `${item.name}\0${item.size}\0${item.digest}`).join('\n'))
  return `dashboard:mail-upload-sha256:${await sha256(encoded)}`
}

export function attachmentPresentation(value = {}) {
  const status = value.extraction_status || 'not_applicable'
  if (status === 'extracted') return { label: 'Text extracted', tone: 'success' }
  if (status === 'partial') return { label: 'Partially extracted', tone: 'warning' }
  if (status === 'empty') return { label: 'No embedded text', tone: 'neutral' }
  if (status === 'unsupported' || status === 'rejected' || status === 'failed') {
    return { label: status, tone: 'danger' }
  }
  return { label: 'Stored', tone: 'neutral' }
}

export function referenceResolutionPresentation(value = {}) {
  if (value.status === 'resolved') return { label: 'Resolved', tone: 'success' }
  if (value.status === 'ambiguous') return { label: 'Ambiguous', tone: 'warning' }
  return { label: 'Unresolved', tone: 'danger' }
}

export function remittanceCorrelationPresentation(value = {}) {
  if (value.status === 'correlated') return { label: 'Correlated', tone: 'success' }
  if (value.status === 'ambiguous') return { label: 'Ambiguous', tone: 'warning' }
  if (value.status === 'candidate_only') return { label: 'Candidate only', tone: 'warning' }
  if (value.status === 'search_incomplete') return { label: 'Search incomplete', tone: 'danger' }
  if (value.status === 'insufficient_facts') return { label: 'Insufficient facts', tone: 'warning' }
  if (value.status === 'not_found') return { label: 'No bank transaction found', tone: 'neutral' }
  return { label: 'Legacy · not evaluated', tone: 'neutral' }
}

export function remittanceInvoiceEvidencePresentation(value = {}) {
  if (value.mode === 'identity_backed') return { label: 'Identity-backed candidates', tone: 'success', reviewOnly: false }
  if (value.mode === 'amount_review') return { label: 'Amount-only suggestions', tone: 'warning', reviewOnly: true }
  if (value.mode === 'non_customer_movement') return { label: 'Non-customer movement', tone: 'warning', reviewOnly: true }
  if (value.mode === 'no_candidates') return { label: 'No invoice suggestions', tone: 'neutral', reviewOnly: true }
  return { label: 'Legacy candidate evidence', tone: 'neutral', reviewOnly: true }
}

const DECISION_GATES = new Set([
  'invoice_reference_resolution',
  'single_customer',
  'remittance_fact_grounding',
  'bank_transaction_correlation',
  'payment_candidate_safety',
])

export function mailGatePresentation(value = {}) {
  const decision = DECISION_GATES.has(value.name)
  return {
    group: decision ? 'Decision gate' : 'Processing gate',
    tone: value.passed ? 'success' : decision ? 'warning' : 'danger',
  }
}
